<div class="headline">
  <span class="mat-h1 m0">Sammelmappen</span>
  <button (click)="create()" color="primary" mat-flat-button>
    <mat-icon>create_new_folder</mat-icon>
    Neue Sammelmappe
  </button>
</div>
<hr>

<div class="folder-page">
  @if (pendingFolders.length > 0) {
    <section class="pending mat-elevation">
      <h2 class="pending-title">Mit Ihnen geteilt – noch nicht angenommen ({{ pendingFolders.length }})</h2>
      <p class="gray m0">
        Sie können diese Sammelmappen bereits ansehen, exportieren und duplizieren.
        Nach dem Annehmen erscheinen sie in Ihrer Übersicht.
      </p>
      <ul class="pending-list">
        @for (folder of pendingFolders; track folder.id) {
          <li class="pending-item">
            <div class="pending-info">
              <a [routerLink]="['/sammelmappen', folder.id]" class="folder-link">{{ folder.name }}</a>
              <span class="gray pending-meta">
                von {{ folderUserName(folder.owner) }} · {{ folder.itemCount }}/{{ maxItems }}
                Einträge · {{ accessLabels[folder.access] }}
              </span>
            </div>
            <div class="pending-actions">
              <button (click)="leave(folder, true)" mat-stroked-button>Ablehnen</button>
              <button (click)="accept(folder)" color="primary" mat-flat-button>Annehmen</button>
            </div>
          </li>
        }
      </ul>
    </section>
  }

  <section class="mat-elevation list-section">
    <div class="filter-bar">
      <mat-button-toggle-group (change)="setScope($event.value)" [value]="scope" aria-label="Sammelmappen filtern">
        <mat-button-toggle value="ALL">Alle</mat-button-toggle>
        <mat-button-toggle value="OWNED">Eigene</mat-button-toggle>
        <mat-button-toggle value="SHARED">Mit mir geteilt</mat-button-toggle>
      </mat-button-toggle-group>
      <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
        <mat-label>Name suchen</mat-label>
        <input [formControl]="searchCtrl" matInput type="search">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    </div>

    <div class="custom-scrollbar table-scroll">
      <table [dataSource]="folders" class="w100" mat-table matSort matSortActive="name" matSortDirection="asc"
             matSortDisableClear>
        <ng-container matColumnDef="name">
          <th *matHeaderCellDef mat-header-cell mat-sort-header>Name</th>
          <td *matCellDef="let folder" mat-cell>
            <a [routerLink]="['/sammelmappen', folder.id]" class="folder-link">
              <mat-icon class="folder-icon">{{ folder.access === 'OWNER' ? 'folder' : 'folder_shared' }}</mat-icon>
              {{ folder.name }}
            </a>
          </td>
        </ng-container>

        <ng-container matColumnDef="itemCount">
          <th *matHeaderCellDef mat-header-cell>Einträge</th>
          <td *matCellDef="let folder" mat-cell>{{ folder.itemCount }}/{{ maxItems }}</td>
        </ng-container>

        <ng-container matColumnDef="owner">
          <th *matHeaderCellDef mat-header-cell>Eigentümer</th>
          <td *matCellDef="let folder" mat-cell>
            {{ folder.access === 'OWNER' ? 'Ich' : folderUserName(folder.owner) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="access">
          <th *matHeaderCellDef mat-header-cell>Berechtigung</th>
          <td *matCellDef="let folder" mat-cell>{{ accessLabels[folder.access] }}</td>
        </ng-container>

        <ng-container matColumnDef="shareCount">
          <th *matHeaderCellDef mat-header-cell>Geteilt</th>
          <td *matCellDef="let folder" mat-cell>
            @if (folder.access === 'OWNER' && folder.shareCount > 0) {
              <span class="share-count" matTooltip="Mit {{ folder.shareCount }} {{ folder.shareCount === 1 ? 'Person' : 'Personen' }} geteilt">
                <mat-icon inline>group</mat-icon> {{ folder.shareCount }}
              </span>
            } @else {
              <span class="gray">–</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="createdAt">
          <th *matHeaderCellDef mat-header-cell mat-sort-header>Erstellt</th>
          <td *matCellDef="let folder" mat-cell>{{ folder.createdAt | date: 'dd.MM.yyyy' }}</td>
        </ng-container>

        <ng-container matColumnDef="updatedAt">
          <th *matHeaderCellDef mat-header-cell mat-sort-header>Geändert</th>
          <td *matCellDef="let folder" mat-cell>{{ folder.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th *matHeaderCellDef mat-header-cell></th>
          <td *matCellDef="let folder" mat-cell class="actions-cell">
            <button (click)="$event.stopPropagation()" [matMenuTriggerFor]="actions" aria-label="Aktionen"
                    mat-icon-button>
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #actions="matMenu" xPosition="before">
              <button (click)="open(folder)" mat-menu-item>
                <mat-icon>folder_open</mat-icon>
                <span>Öffnen</span>
              </button>
              @if (canEdit(folder)) {
                <button (click)="rename(folder)" mat-menu-item>
                  <mat-icon>edit</mat-icon>
                  <span>Umbenennen</span>
                </button>
              }
              <button (click)="duplicate(folder)" mat-menu-item>
                <mat-icon>content_copy</mat-icon>
                <span>Duplizieren</span>
              </button>
              <button (click)="export(folder)" mat-menu-item>
                <mat-icon>download</mat-icon>
                <span>Exportieren</span>
              </button>
              @if (folder.access === 'OWNER') {
                <button (click)="share(folder)" mat-menu-item>
                  <mat-icon>share</mat-icon>
                  <span>Teilen</span>
                </button>
                <button (click)="delete(folder)" mat-menu-item>
                  <mat-icon color="warn">delete</mat-icon>
                  <span>Löschen</span>
                </button>
              } @else {
                <button (click)="leave(folder)" mat-menu-item>
                  <mat-icon color="warn">folder_off</mat-icon>
                  <span>Aus meinen Sammelmappen entfernen</span>
                </button>
              }
            </mat-menu>
          </td>
        </ng-container>

        <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
        <tr (click)="open(folder)" *matRowDef="let folder; columns: displayedColumns;" class="folder-row" mat-row></tr>

        <tr *matNoDataRow class="mat-row">
          <td [attr.colspan]="displayedColumns.length" class="mat-cell">
            @if (!loading) {
              <div class="pad16 no-data">
                @if (searchCtrl.value) {
                  Keine Sammelmappe gefunden.
                } @else if (scope === 'SHARED') {
                  Es wurden noch keine Sammelmappen mit Ihnen geteilt.
                } @else {
                  Sie haben noch keine Sammelmappen. Legen Sie eine neue an oder wählen Sie in den Tabellen
                  (z.&nbsp;B. im Wortschatz) Einträge aus und fügen Sie sie einer Sammelmappe hinzu.
                }
              </div>
            }
          </td>
        </tr>
      </table>
    </div>
    @if (loading) {
      <div class="center-content pad16">
        <mat-progress-spinner diameter="40" mode="indeterminate"></mat-progress-spinner>
      </div>
    }
    <mat-paginator [length]="totalElements" [pageSizeOptions]="[25, 50, 100]" [pageSize]="25"
                   showFirstLastButtons></mat-paginator>
  </section>
</div>
